import { detectIntent } from './detector';
import { clientEnvironment } from './environment';
import { clickIds, identityState, mysiteAttribution, utmParams } from './identity';
import { sha256Hex } from './hash';
import { localTimeParts } from './time';
import { sendEvent, uuid } from './transport';
import type { ConversionIntentEvent, SiteConfig } from './types';

const NORMALIZATION_VERSION = 'intent-map@2026-06-16';
const DEDUPE_WINDOW_MS = 1500;
const sentFingerprints = new Map<string, number>();

export async function collectElementIntent(element: Element, config: SiteConfig, captureMethod: ConversionIntentEvent['capture_method']): Promise<void> {
  const detection = await detectIntent(element);
  if (!detection || detection.canonical_intent === 'other') return;

  const now = new Date();
  const identity = identityState(now);
  const env = clientEnvironment();
  const targetHash = detection.target ? await sha256Hex(detection.target) : null;
  const fingerprint = await sha256Hex([detection.canonical_intent, detection.provider, detection.evidence.dom_path_hash, detection.target ?? ''].join('|'));
  if (isDuplicate(fingerprint, now.getTime())) return;

  const event: ConversionIntentEvent = {
    schema_version: 1,
    event_type: 'conversion_intent',
    event_id: uuid(),
    source: 'embed',
    capture_method: captureMethod,
    confidence: detection.confidence,
    site_id: config.siteId,
    org_id: config.orgId,
    location_id: config.locationId,
    restaurant_country: config.restaurantCountry,
    restaurant_city: config.restaurantCity,
    canonical_intent: detection.canonical_intent,
    provider: detection.provider,
    provider_category: detection.provider_category,
    target: detection.target,
    target_hash: targetHash,
    visitor_id: identity.visitor_id,
    session_id: identity.session_id,
    session_started_at_utc: identity.session_started_at_utc,
    source_url: window.location.href,
    hostname: env.hostname,
    referrer: document.referrer || null,
    element_text: detection.evidence.text,
    element_role: detection.evidence.role,
    element_fingerprint: fingerprint,
    screen: env.screen,
    language: env.language,
    mysite_attribution: mysiteAttribution(),
    utm: utmParams(),
    click_ids: clickIds(),
    normalization_version: NORMALIZATION_VERSION,
    matched_rule_id: detection.matched_rule_id,
    event_name_raw: null,
    provider_raw: null,
    ...localTimeParts(now, config.timezone),
  };

  sendEvent(config.collectUrl, event);
}

function isDuplicate(fingerprint: string, nowMs: number): boolean {
  const last = sentFingerprints.get(fingerprint);
  sentFingerprints.set(fingerprint, nowMs);
  for (const [key, value] of sentFingerprints.entries()) {
    if (nowMs - value > DEDUPE_WINDOW_MS) sentFingerprints.delete(key);
  }
  return typeof last === 'number' && nowMs - last < DEDUPE_WINDOW_MS;
}
